import { useTranslation } from 'react-i18next'

import { useMe } from '@/api/hooks/useMe'
import PageHeader from '@/components/molecules/page-header'
import { PlayVideoButton } from '@/components/molecules/play-video-button'
import { InviteUserDialog } from '@/components/organisms/users-table/invite-user-dialog'
import { INVITE_VIDEO } from '@/config/videos'
import { IndividualType } from '@/hooks/useIndividuals'

import { OwnersList } from './owners-list'

type Props = {
  individual: IndividualType
}

/**
 * A friendlier, home-surfaced entry point for inviting co-owners — reuses the
 * existing InviteUserDialog + revoke mutation, with a compact owners list.
 */
export const InviteIndividualCard = ({ individual }: Props) => {
  const { t } = useTranslation()
  const { data: me } = useMe()

  // Don't list the current user among the invited co-owners.
  const otherUsers = individual.users.filter(u => u.id !== me?.id)

  // The creator, who is the only one who may invite or revoke. `createIndividual` gives
  // them the 'admin' role; everyone brought in afterwards gets none.
  const isAdmin = !!individual.users.find(u => u.id === me?.id)?.roles?.includes('admin')

  return (
    <section className="space-y-4">
      {/* For a co-owner this stops being an invitation and becomes a statement of who
          else can see the profile. Every string here is written from the inviter's side
          — "Bjud in fler", "Du har inte bjudit in några än" — so leaving them in place
          would instruct somebody to do the one thing they are not allowed to. The button
          is hidden rather than disabled for the same reason: a greyed-out control asks how
          to earn it, and the answer is that they cannot. */}
      <PageHeader
        body={t(isAdmin ? 'homePage.invite.description' : 'homePage.invite.readOnlyDescription')}
        heading={t(isAdmin ? 'homePage.invite.title' : 'homePage.invite.readOnlyTitle')}
        titleAction={
          isAdmin ? (
            <PlayVideoButton titleKey={INVITE_VIDEO.titleKey} youtubeId={INVITE_VIDEO.youtubeId} />
          ) : undefined
        }
      >
        {isAdmin && <InviteUserDialog individualId={individual.id} />}
      </PageHeader>

      {otherUsers.length > 0 ? (
        <OwnersList canManage={isAdmin} individualId={individual.id} owners={otherUsers} />
      ) : (
        <div className="rounded-lg border border-dashed border-subtle-01 px-4 py-6 text-center">
          <p className="font-medium">{t('homePage.invite.emptyTitle')}</p>
          <p className="text-text-secondary text-base">
            {t(isAdmin ? 'homePage.invite.emptyBody' : 'homePage.invite.readOnlyEmptyBody')}
          </p>
        </div>
      )}
    </section>
  )
}

export default InviteIndividualCard
